import { useState, useEffect } from "react";
import { Layout } from "../components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "../components/ui/dialog";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import {
  createPersonalInvoice,
  listPersonalInvoices,
  getPersonalInvoicePdfUrl,
  getBusinessDetails,
  updateBusinessDetails,
  generateInvoicePaymentLink,
  getInvoicePaymentLink
} from "../lib/api";
import {
  FileText,
  Plus,
  Download,
  Send,
  Link2,
  Copy,
  Check,
  Loader2,
  Building2,
  Mail,
  MapPin,
  Phone,
  Hash,
  Trash2,
  ExternalLink,
  CheckCircle,
  Clock,
  AlertCircle
} from "lucide-react";

const CURRENCIES = ['USD', 'EUR', 'GBP', 'NGN', 'KES', 'GHS', 'ZAR', 'JPY', 'CNY', 'INR', 'CAD', 'AUD'];

export default function Invoices() {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showBusinessDialog, setShowBusinessDialog] = useState(false);
  const [businessDetails, setBusinessDetails] = useState({});
  const [savingBusiness, setSavingBusiness] = useState(false);
  const [copiedLink, setCopiedLink] = useState(null);
  const [generatingLink, setGeneratingLink] = useState(null);
  
  // New invoice form
  const [newInvoice, setNewInvoice] = useState({
    recipient_name: "",
    recipient_email: "",
    recipient_address: "",
    currency: "USD",
    tax_rate: 0,
    notes: "",
    line_items: [{ description: "", quantity: 1, unit_price: 0 }]
  });
  
  // Business details form
  const [businessForm, setBusinessForm] = useState({
    business_name: "",
    tax_id: "",
    address: "",
    phone: "",
    email: ""
  });

  useEffect(() => {
    fetchInvoices();
    fetchBusinessDetails();
  }, []);

  const fetchInvoices = async () => {
    try {
      setLoading(true);
      const response = await listPersonalInvoices(50, 0);
      setInvoices(response.data.invoices || []);
    } catch (error) {
      toast.error("Failed to load invoices");
    } finally {
      setLoading(false);
    }
  };

  const fetchBusinessDetails = async () => {
    try {
      const response = await getBusinessDetails();
      const details = response.data.business_details || {};
      setBusinessDetails(details);
      setBusinessForm(details);
    } catch (error) {
      console.error("Failed to fetch business details:", error);
    }
  };

  const handleCreateInvoice = async () => {
    // Validate
    if (!newInvoice.line_items.some(item => item.description && item.unit_price > 0)) {
      toast.error("Please add at least one line item with description and price");
      return;
    }

    try {
      setCreating(true);
      await createPersonalInvoice(newInvoice);
      toast.success("Invoice created successfully");
      setShowCreateDialog(false);
      resetInvoiceForm();
      fetchInvoices();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create invoice");
    } finally {
      setCreating(false);
    }
  };

  const resetInvoiceForm = () => {
    setNewInvoice({
      recipient_name: "",
      recipient_email: "",
      recipient_address: "",
      currency: "USD",
      tax_rate: 0,
      notes: "",
      line_items: [{ description: "", quantity: 1, unit_price: 0 }]
    });
  };

  const handleSaveBusinessDetails = async () => {
    try {
      setSavingBusiness(true);
      await updateBusinessDetails(businessForm);
      setBusinessDetails(businessForm);
      toast.success("Business details saved");
      setShowBusinessDialog(false);
    } catch (error) {
      toast.error("Failed to save business details");
    } finally {
      setSavingBusiness(false);
    }
  };

  const addLineItem = () => {
    setNewInvoice(prev => ({
      ...prev,
      line_items: [...prev.line_items, { description: "", quantity: 1, unit_price: 0 }]
    }));
  };

  const removeLineItem = (index) => {
    if (newInvoice.line_items.length <= 1) return;
    setNewInvoice(prev => ({
      ...prev,
      line_items: prev.line_items.filter((_, i) => i !== index)
    }));
  };

  const updateLineItem = (index, field, value) => {
    setNewInvoice(prev => ({
      ...prev,
      line_items: prev.line_items.map((item, i) => 
        i === index ? { ...item, [field]: field === 'quantity' || field === 'unit_price' ? parseFloat(value) || 0 : value } : item
      )
    }));
  };

  const calculateTotal = () => {
    const subtotal = newInvoice.line_items.reduce((sum, item) => sum + (item.quantity * item.unit_price), 0);
    const tax = subtotal * (newInvoice.tax_rate / 100);
    return { subtotal, tax, total: subtotal + tax };
  };

  const handleGeneratePaymentLink = async (invoiceId) => {
    try {
      setGeneratingLink(invoiceId);
      const response = await generateInvoicePaymentLink(invoiceId);
      const { payment_link } = response.data;
      
      // Update invoice in list with payment link
      setInvoices(prev => prev.map(inv => 
        inv.id === invoiceId 
          ? { ...inv, payment_token: response.data.payment_token, payment_token_expires_at: response.data.expires_at }
          : inv
      ));
      
      // Copy to clipboard
      const fullLink = `${window.location.origin}${payment_link}`;
      await navigator.clipboard.writeText(fullLink);
      setCopiedLink(invoiceId);
      setTimeout(() => setCopiedLink(null), 3000);
      
      toast.success("Payment link generated and copied!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to generate payment link");
    } finally {
      setGeneratingLink(null);
    }
  };

  const handleCopyExistingLink = async (invoice) => {
    const fullLink = `${window.location.origin}/invoice/pay/${invoice.payment_token}`;
    await navigator.clipboard.writeText(fullLink);
    setCopiedLink(invoice.id);
    setTimeout(() => setCopiedLink(null), 3000);
    toast.success("Payment link copied!");
  };

  const currencySymbols = {
    USD: '$', EUR: '€', GBP: '£', NGN: '₦', KES: 'KSh', 
    GHS: '₵', ZAR: 'R', JPY: '¥', CNY: '¥', INR: '₹',
    CAD: 'C$', AUD: 'A$'
  };

  const formatCurrency = (amount, currency) => {
    const symbol = currencySymbols[currency] || currency + ' ';
    return `${symbol}${amount?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00'}`;
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'paid':
        return (
          <span className="flex items-center gap-1 text-emerald-400 text-xs bg-emerald-500/20 px-2 py-1 rounded-full">
            <CheckCircle className="h-3 w-3" /> Paid
          </span>
        );
      case 'issued':
        return (
          <span className="flex items-center gap-1 text-[#D4AF37] text-xs bg-[#D4AF37]/20 px-2 py-1 rounded-full">
            <Clock className="h-3 w-3" /> Pending
          </span>
        );
      default:
        return (
          <span className="flex items-center gap-1 text-gray-400 text-xs bg-gray-500/20 px-2 py-1 rounded-full">
            <AlertCircle className="h-3 w-3" /> {status}
          </span>
        );
    }
  };

  const totals = calculateTotal();

  return (
    <Layout>
      <div className="space-y-6 max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl font-semibold text-white flex items-center gap-2">
              <FileText className="h-6 w-6 text-[#D4AF37]" />
              Invoices
            </h1>
            <p className="text-gray-400 text-sm mt-1">Create and manage invoices with shareable payment links</p>
          </div>
          <div className="flex gap-3">
            <Dialog open={showBusinessDialog} onOpenChange={setShowBusinessDialog}>
              <DialogTrigger asChild>
                <Button variant="outline" className="border-gray-600 text-gray-300 hover:bg-gray-700">
                  <Building2 className="h-4 w-4 mr-2" />
                  Business Details
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-[#232342] border-gray-700 text-white max-w-md">
                <DialogHeader>
                  <DialogTitle className="text-white">Business Details</DialogTitle>
                  <DialogDescription className="text-gray-400">
                    Your business information will appear on invoices you create.
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                  <div>
                    <Label className="text-gray-300">Business Name</Label>
                    <Input
                      value={businessForm.business_name || ""}
                      onChange={(e) => setBusinessForm(prev => ({ ...prev, business_name: e.target.value }))}
                      placeholder="Your Company Name"
                      className="bg-[#1a1a2e] border-gray-600 text-white"
                    />
                  </div>
                  <div>
                    <Label className="text-gray-300">Tax ID / VAT Number</Label>
                    <Input
                      value={businessForm.tax_id || ""}
                      onChange={(e) => setBusinessForm(prev => ({ ...prev, tax_id: e.target.value }))}
                      placeholder="e.g., VAT123456789"
                      className="bg-[#1a1a2e] border-gray-600 text-white"
                    />
                  </div>
                  <div>
                    <Label className="text-gray-300">Business Address</Label>
                    <Textarea
                      value={businessForm.address || ""}
                      onChange={(e) => setBusinessForm(prev => ({ ...prev, address: e.target.value }))}
                      placeholder="123 Business Street, City, Country"
                      className="bg-[#1a1a2e] border-gray-600 text-white"
                      rows={2}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-gray-300">Phone</Label>
                      <Input
                        value={businessForm.phone || ""}
                        onChange={(e) => setBusinessForm(prev => ({ ...prev, phone: e.target.value }))}
                        placeholder="+1 234 567 8900"
                        className="bg-[#1a1a2e] border-gray-600 text-white"
                      />
                    </div>
                    <div>
                      <Label className="text-gray-300">Email</Label>
                      <Input
                        value={businessForm.email || ""}
                        onChange={(e) => setBusinessForm(prev => ({ ...prev, email: e.target.value }))}
                        placeholder="billing@company.com"
                        className="bg-[#1a1a2e] border-gray-600 text-white"
                      />
                    </div>
                  </div>
                  <Button
                    onClick={handleSaveBusinessDetails}
                    disabled={savingBusiness}
                    className="w-full bg-[#D4AF37] hover:bg-[#B8860B] text-white"
                  >
                    {savingBusiness ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                    Save Details
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
            
            <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
              <DialogTrigger asChild>
                <Button className="bg-[#D4AF37] hover:bg-[#B8860B] text-white" data-testid="create-invoice-btn">
                  <Plus className="h-4 w-4 mr-2" />
                  Create Invoice
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-[#232342] border-gray-700 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="text-white">Create Invoice</DialogTitle>
                  <DialogDescription className="text-gray-400">
                    Add line items and recipient details to generate a shareable invoice.
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-6 mt-4">
                  {/* Recipient Info */}
                  <div className="space-y-4">
                    <h3 className="text-sm font-medium text-gray-400 uppercase tracking-wider">Bill To</h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-gray-300">Name</Label>
                        <Input
                          value={newInvoice.recipient_name}
                          onChange={(e) => setNewInvoice(prev => ({ ...prev, recipient_name: e.target.value }))}
                          placeholder="Client name"
                          className="bg-[#1a1a2e] border-gray-600 text-white"
                        />
                      </div>
                      <div>
                        <Label className="text-gray-300">Email</Label>
                        <Input
                          type="email"
                          value={newInvoice.recipient_email}
                          onChange={(e) => setNewInvoice(prev => ({ ...prev, recipient_email: e.target.value }))}
                          placeholder="client@email.com"
                          className="bg-[#1a1a2e] border-gray-600 text-white"
                        />
                      </div>
                    </div>
                    <div>
                      <Label className="text-gray-300">Address (optional)</Label>
                      <Input
                        value={newInvoice.recipient_address}
                        onChange={(e) => setNewInvoice(prev => ({ ...prev, recipient_address: e.target.value }))}
                        placeholder="Client address"
                        className="bg-[#1a1a2e] border-gray-600 text-white"
                      />
                    </div>
                  </div>

                  {/* Line Items */}
                  <div className="space-y-4">
                    <h3 className="text-sm font-medium text-gray-400 uppercase tracking-wider">Items</h3>
                    {newInvoice.line_items.map((item, index) => (
                      <div key={index} className="flex gap-3 items-start">
                        <div className="flex-1">
                          <Input
                            value={item.description}
                            onChange={(e) => updateLineItem(index, 'description', e.target.value)}
                            placeholder="Description"
                            className="bg-[#1a1a2e] border-gray-600 text-white"
                          />
                        </div>
                        <div className="w-20">
                          <Input
                            type="number"
                            value={item.quantity}
                            onChange={(e) => updateLineItem(index, 'quantity', e.target.value)}
                            placeholder="Qty"
                            min="1"
                            className="bg-[#1a1a2e] border-gray-600 text-white"
                          />
                        </div>
                        <div className="w-28">
                          <Input
                            type="number"
                            value={item.unit_price || ""}
                            onChange={(e) => updateLineItem(index, 'unit_price', e.target.value)}
                            placeholder="Price"
                            min="0"
                            step="0.01"
                            className="bg-[#1a1a2e] border-gray-600 text-white"
                          />
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => removeLineItem(index)}
                          disabled={newInvoice.line_items.length <= 1}
                          className="text-gray-400 hover:text-red-400"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                    <Button
                      variant="outline"
                      onClick={addLineItem}
                      className="border-dashed border-gray-600 text-gray-400 hover:text-white w-full"
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Add Item
                    </Button>
                  </div>

                  {/* Currency & Tax */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-gray-300">Currency</Label>
                      <Select
                        value={newInvoice.currency}
                        onValueChange={(value) => setNewInvoice(prev => ({ ...prev, currency: value }))}
                      >
                        <SelectTrigger className="bg-[#1a1a2e] border-gray-600 text-white">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-[#232342] border-gray-700">
                          {CURRENCIES.map(c => (
                            <SelectItem key={c} value={c} className="text-white hover:bg-[#1a1a2e]">
                              {c}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-gray-300">Tax Rate (%)</Label>
                      <Input
                        type="number"
                        value={newInvoice.tax_rate}
                        onChange={(e) => setNewInvoice(prev => ({ ...prev, tax_rate: parseFloat(e.target.value) || 0 }))}
                        placeholder="0"
                        min="0"
                        max="100"
                        className="bg-[#1a1a2e] border-gray-600 text-white"
                      />
                    </div>
                  </div>

                  {/* Notes */}
                  <div>
                    <Label className="text-gray-300">Notes (optional)</Label>
                    <Textarea
                      value={newInvoice.notes}
                      onChange={(e) => setNewInvoice(prev => ({ ...prev, notes: e.target.value }))}
                      placeholder="Payment terms, thank you message, etc."
                      className="bg-[#1a1a2e] border-gray-600 text-white"
                      rows={2}
                    />
                  </div>

                  {/* Totals */}
                  <div className="bg-[#1a1a2e] rounded-lg p-4 space-y-2">
                    <div className="flex justify-between text-gray-400 text-sm">
                      <span>Subtotal</span>
                      <span>{formatCurrency(totals.subtotal, newInvoice.currency)}</span>
                    </div>
                    {newInvoice.tax_rate > 0 && (
                      <div className="flex justify-between text-gray-400 text-sm">
                        <span>Tax ({newInvoice.tax_rate}%)</span>
                        <span>{formatCurrency(totals.tax, newInvoice.currency)}</span>
                      </div>
                    )}
                    <div className="flex justify-between text-white font-semibold text-lg pt-2 border-t border-gray-700">
                      <span>Total</span>
                      <span className="text-[#D4AF37]">{formatCurrency(totals.total, newInvoice.currency)}</span>
                    </div>
                  </div>

                  <Button
                    onClick={handleCreateInvoice}
                    disabled={creating}
                    className="w-full bg-[#D4AF37] hover:bg-[#B8860B] text-white h-12"
                    data-testid="submit-invoice-btn"
                  >
                    {creating ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <FileText className="h-4 w-4 mr-2" />}
                    Create Invoice
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Invoices List */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-8 w-8 animate-spin text-[#D4AF37]" />
          </div>
        ) : invoices.length === 0 ? (
          <Card className="bg-[#232342]/80 border-[#D4AF37]/20">
            <CardContent className="flex flex-col items-center justify-center py-16">
              <FileText className="h-16 w-16 text-gray-600 mb-4" />
              <h3 className="text-lg font-medium text-gray-400 mb-2">No invoices yet</h3>
              <p className="text-gray-500 text-sm mb-6">Create your first invoice to get started</p>
              <Button
                onClick={() => setShowCreateDialog(true)}
                className="bg-[#D4AF37] hover:bg-[#B8860B] text-white"
              >
                <Plus className="h-4 w-4 mr-2" />
                Create Invoice
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            <AnimatePresence>
              {invoices.map((invoice, index) => (
                <motion.div
                  key={invoice.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ delay: index * 0.05 }}
                >
                  <Card className="bg-[#232342]/80 border-[#D4AF37]/10 hover:border-[#D4AF37]/30 transition-colors">
                    <CardContent className="p-4 sm:p-6">
                      <div className="flex flex-col sm:flex-row justify-between gap-4">
                        {/* Invoice Info */}
                        <div className="flex-1 space-y-2">
                          <div className="flex items-center gap-3 flex-wrap">
                            <span className="font-mono text-white font-medium">{invoice.invoice_number}</span>
                            {getStatusBadge(invoice.status)}
                          </div>
                          <div className="text-gray-400 text-sm">
                            {invoice.recipient?.name || invoice.recipient?.email || "No recipient"}
                          </div>
                          <div className="text-gray-500 text-xs">
                            Created: {new Date(invoice.created_at).toLocaleDateString()}
                          </div>
                        </div>

                        {/* Amount */}
                        <div className="text-right">
                          <div className="text-xl font-semibold text-[#D4AF37]">
                            {formatCurrency(invoice.total, invoice.currency)}
                          </div>
                          <div className="text-gray-500 text-sm">{invoice.currency}</div>
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-gray-700/50">
                        {/* Download PDF */}
                        <Button
                          variant="outline"
                          size="sm"
                          className="border-gray-600 text-gray-300 hover:bg-gray-700"
                          onClick={() => window.open(getPersonalInvoicePdfUrl(invoice.id), '_blank')}
                        >
                          <Download className="h-4 w-4 mr-1" />
                          PDF
                        </Button>

                        {/* Payment Link */}
                        {invoice.status !== 'paid' && (
                          <>
                            {invoice.payment_token ? (
                              <Button
                                variant="outline"
                                size="sm"
                                className={copiedLink === invoice.id 
                                  ? "border-emerald-500 text-emerald-400 bg-emerald-500/10" 
                                  : "border-[#D4AF37]/50 text-[#D4AF37] hover:bg-[#D4AF37]/10"
                                }
                                onClick={() => handleCopyExistingLink(invoice)}
                                data-testid={`copy-link-${invoice.id}`}
                              >
                                {copiedLink === invoice.id ? (
                                  <>
                                    <Check className="h-4 w-4 mr-1" />
                                    Copied!
                                  </>
                                ) : (
                                  <>
                                    <Copy className="h-4 w-4 mr-1" />
                                    Copy Link
                                  </>
                                )}
                              </Button>
                            ) : (
                              <Button
                                variant="outline"
                                size="sm"
                                className="border-[#D4AF37]/50 text-[#D4AF37] hover:bg-[#D4AF37]/10"
                                onClick={() => handleGeneratePaymentLink(invoice.id)}
                                disabled={generatingLink === invoice.id}
                                data-testid={`generate-link-${invoice.id}`}
                              >
                                {generatingLink === invoice.id ? (
                                  <>
                                    <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                                    Generating...
                                  </>
                                ) : (
                                  <>
                                    <Link2 className="h-4 w-4 mr-1" />
                                    Get Payment Link
                                  </>
                                )}
                              </Button>
                            )}

                            {/* Open payment page */}
                            {invoice.payment_token && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-gray-400 hover:text-white"
                                onClick={() => window.open(`/invoice/pay/${invoice.payment_token}`, '_blank')}
                              >
                                <ExternalLink className="h-4 w-4" />
                              </Button>
                            )}
                          </>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        )}
      </div>
    </Layout>
  );
}
